/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2014-2018, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

angular.module('bulbApp.authModule')
  .controller('SignInController', [
    '$cookies',
    '$scope',
    '$state',
    '$stateParams',
    '$translate',
    '$window',
    'google',
    'BulbAuthService',
    'BulbBrowserService',
    'BulbBusyService',
    'BulbErrorService',
    'BulbModalService',
    'PushNotificationService',
    'Restangular',
    'CONFIG',
    '_',
    SignInController,
  ]);

function SignInController(
  $cookies,
  $scope,
  $state,
  $stateParams,
  $translate,
  $window,
  google,
  BulbAuthService,
  BulbBrowserService,
  BulbBusyService,
  BulbErrorService,
  BulbModalService,
  PushNotificationService,
  Restangular,
  CONFIG,
  _,
) {
  const that = this;

  this.cleverIntegrationEnabled = CONFIG.features.cleverIntegrationEnabled;

  this.credentials = {
    usernameOrEmail: '',
    password: '',
  };

  this.signInUnsuccessful = false;
  this.signInWithGoogleInProgress = false;
  this.googleSignInReady = false;

  this.currentYear = new Date().getFullYear();

  this.createAccountTranslation = $translate.instant('sign_in_or_create_new_account');

  // Boolean to check if our app is running as a hosted webapp, PWA.
  this.runningInPWAEnvironment = BulbBrowserService.isPWAEnvironment();
  this.isMultiFactorLogin = $stateParams.mfa;
  // Boolean to check if our app is running from icev url
  this.icev = $stateParams.icev;
  // If we are on the exp sign-up page.
  this.exp = $stateParams.exp || $cookies.get('expSignup');

  if ($stateParams.redirected) {
    this.redirected = $stateParams.redirected;
  } else {
    this.redirected = false;
  }
  // Load the gapi auth2
  if (google.load) {
    google.load('auth2', () => {
      that.googleSignInReady = true;
    });
  }

  /**
   * Sign in the user using the credentials provided via the signIn form.
   */
  this.signIn = function () {
    this.signInUnsuccessful = false;
    BulbBusyService.showBusyDuring(
      BulbAuthService.signIn(that.credentials).then(
        doChecksThenRedirectAfterSignIn,
        (error) => {
          // FUTURE : this might be better tied in with the form validation directive/errors,
          // but it was simpler to do this way for now.
          that.signInUnsuccessful = true;
        },
      ),
    );
  };

  // Redirect the user to the create account / sign-up page.
  this.redirectToCreateAccount = function (event) {
    if (event.type === 'click') {
      $state.go('signUp', { targetUrl: $stateParams.targetUrl, referrer: $stateParams.referrer });
    } else if (event.key === 'Enter' || event.key === ' ') {
      $state.go('signUp', { redirected: true });
    }
  };

  // Redirect the user to the reset password page.
  this.redirectToResetPassword = function (event) {
    if (event.type === 'click') {
      $state.go('resetPassword');
    } else if (event.key === 'Enter' || event.key === ' ') {
      $state.go('resetPassword', { redirected: true });
    }
  };

  /**
   * Sign in the user via microsoft authentication.
   */
  this.signInWithGoogle = function () {
    const params = $stateParams.targetUrl ? `&targetUrl=${encodeURIComponent($stateParams.targetUrl)}` : '';
    $window.location.href = `/b/auth/google?entryMode=sign-in${params}`;
  };

  /**
   * Sign in the user via microsoft authentication.
   */
  this.signInWithMicrosoft = function () {
    const params = $stateParams.targetUrl ? `&targetUrl=${encodeURIComponent($stateParams.targetUrl)}` : '';
    $window.location.href = `/b/auth/microsoft?entryMode=sign-in${params}`;
  };

  /**
   * Sign in the user via clever authentication.
   */
  this.signInWithClever = function () {
    const params = $stateParams.targetUrl ? `&targetUrl=${encodeURIComponent($stateParams.targetUrl)}` : '';
    $window.location.href = `/b/auth/clever?entryMode=sign-in${params}`;
  };

  /**
   * Check for age verification required before doing the redirect.
   * @param account
   */
  function doChecksThenRedirectAfterSignIn(account) {
    // First, check if the user's age needs to be verified.
    if (isAgeVerificationRequired(account)) {
      // The verification process will call the redirect itself.
      const modalInstance = verifyStudentAge(account);

      modalInstance.result.then(() => {
        BulbAuthService.fetchCurrentUser().then((retrievedAccount) => {
          if (!retrievedAccount.enabled) {
            $window.location = '/';
          } else {
            redirectAfterSignIn(account);
          }
        });
      });
    } else {
      redirectAfterSignIn(account);
    }
  }

  /**
   * Redirects to the user's home page as long as no age verification is necessary.
   * @param account
   */
  function redirectAfterSignIn(account) {
    // FUTURE : we currently do a full reload so that we can refresh permissions on all data for the
    // successful sign in.  In the future, it would be nice to clean/refresh the client state without the
    // reload, but this is the easiest way to handle it for now.
    that.checkPushEnabled(account.notificationPref.pushNotification.enabled, account);
    if ($stateParams.targetUrl) {
      $window.location.href = $stateParams.targetUrl;
    } else {
      $window.location.href = `/${account.username}`;
    }
  }

  /**
   * Displays the modal to the user to input their date of birth.
   * @param account the student's account data.
   */
  function verifyStudentAge(account) {
    // Let the student input their DOB and then store it
    const modalOptions = {
      fontAwesomeCode: 'fas fa-birthday-cake',
      titleCode: 'age_verification_modal_header',
      template: '<bulb-age-verification></bulb-age-verification>',
      windowClass: 'updated-modal-design one-button-modal-design bulb-age-verification-modal',
      backdrop: 'static',
      closeOnClick: false,
      showOkButton: false,
      showCancelButton: false,
    };

    $scope.ageVerifyModalOpen = true;
    return BulbModalService.open(modalOptions);
  }

  /**
   * Analyzes the given account to assess whether or not the age verification is needed.
   * @param account the user data to process.
   */
  function isAgeVerificationRequired(account) {
    let ageVerificationRequired = false;

    // If the process got this far, the account is still enabled, so no need to check that here.
    if (account.position === 'STUDENT' && !isInAnOrganization(account)
      && (!account.prefs.offBoarding || account.prefs.offBoarding === null)
      && !account.ageVerified) {
      ageVerificationRequired = true;
    }

    return ageVerificationRequired;
  }

  /**
   * Determines whether the given account is part of an Organization.
   * @param account  The account to check memberships for.
   */
  function isInAnOrganization(account) {
    return _.countBy(account.groupMemberships, 'type').ORGANIZATION > 0;
  }

  /**
   * Check whether the push notification enabled for user
   * @param pushNotificationEnabled
   * @param userId
   */
  this.checkPushEnabled = function (pushNotificationEnabled, account) {
    // check for safari browser with latest version and push notification enabled for user
    // Open issue with the typescript compiler. See:
    // https://github.com/Microsoft/TypeScript/issues/11498
    // https://github.com/Microsoft/TypeScript/issues/12176
    // @ts-ignore
    if ('safari' in window && 'pushNotification' in window.safari && pushNotificationEnabled
      && PushNotificationService.checkBrowserVersion() >= 11) {
      // @ts-ignore
      const permissionData = window.safari.pushNotification.permission(CONFIG.pushnotification.apns.safari.webPushId);
      if (permissionData.permission === 'default') {
        PushNotificationService.checkRemotePermission(permissionData, account, that.signInForm);
      }
    }
  };

  /**
   * Sends the parental consent email to the email address provided.
   */
  this.sendCOPPAEmail = function () {
    if (that.form.coppaModalEmail !== null) {
      BulbAuthService.fetchCurrentUser().then(() => {
        BulbBusyService.showBusyDuring(
          Restangular.one('/accounts/coppaEmail/').customPOST({ email: that.form.coppaModalEmail })
            .then(() => {
              // This did not redirect as it should have, so doing that ourselves here.
              BulbAuthService.signOut(null, true);
              $window.location.href = '/';
            }).catch((err) => {
              BulbErrorService.showError(null, err);
            }),
        );
      });
    }
  };

  // Redirect the user to icev help page.
  this.redirectToIcev = function () {
    $window.location.href = '//my.bulbapp.com/icev-bulb-sign-up';
  };
}
